import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClubTournaments, deleteTournament } from '../../services/tournamentService';
import { Button, BackButton } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import ConfirmModal from '../common/ConfirmModal';
import { TournamentCard } from './TournamentCard';
import { ClubManagementIconWell } from '../clubs/dashboard/ClubSectionIcons';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../../utils/sessionMobileLayout';

const TOURNAMENTS_PAGE_SHELL = `${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`;
const TOURNAMENTS_PAGE_INNER = `mx-auto max-w-app-shell ${APP_PAGE_GUTTER} pt-3 pb-8 md:pt-6`;

/**
 * @param {Object} props
 * @param {string} props.clubId
 * @param {boolean} props.canManageTournaments - owner, admin, or host: create/delete in management (layout=page) only
 * @param {string|null|undefined} props.seasonId - filter; null/undefined = all seasons
 * @param {'page' | 'embed'} props.layout - full page (back button, min layout) vs club tab
 * @param {string} [props.backButtonLabel] - for layout=page, back button children
 * @param {() => void} [props.onBack] - for layout=page
 */
const ClubTournamentList = ({
  clubId,
  canManageTournaments,
  seasonId = null,
  layout = 'embed',
  backButtonLabel = 'Back to Club Management',
  onBack,
}) => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTournaments = useCallback(async () => {
    if (!clubId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getClubTournaments(clubId, seasonId || null);
      setTournaments(data || []);
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clubId, seasonId]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  const handleDeleteTournament = async () => {
    if (!tournamentToDelete) return;
    try {
      setDeleting(true);
      setError(null);
      await deleteTournament(tournamentToDelete.id);
      setTournamentToDelete(null);
      await fetchTournaments();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(`/club-management/${clubId}`);
    }
  };

  const handleViewTournament = (tournament) => {
    navigate(`/club/${clubId}/tournaments/${tournament.id}`);
  };

  const showCreateActions = canManageTournaments && layout === 'page';

  const tournamentGridClass =
    layout === 'embed'
      ? `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:pb-0 sm:pt-0 lg:grid-cols-3 sm:snap-none`
      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';

  const tournamentCardClass =
    layout === 'embed'
      ? 'shrink-0 w-[min(100%,18.5rem)] sm:w-auto sm:shrink snap-start'
      : '';

  const listBody = (
    <>
      {error && (
        <Card variant="danger" size="small" className="mb-6">
          <Typography variant="body">{error}</Typography>
        </Card>
      )}

      {tournaments.length === 0 ? (
        <Card variant="default" size="medium" className="flex flex-col items-center px-6 py-10 text-center">
          <ClubManagementIconWell type="tournaments" />
          <Heading level={3} className="mb-2 mt-4">
            No tournaments {seasonId ? 'this season' : 'yet'}
          </Heading>
          <Text className="mb-6 max-w-md text-text-secondary">
            {showCreateActions
              ? 'Create a tournament to get started.'
              : 'Your club has not created a tournament yet.'}
          </Text>
          {showCreateActions && (
            <Button
              variant="primary"
              onClick={() => navigate(`/club/${clubId}/tournaments/new`)}
            >
              Create Tournament
            </Button>
          )}
        </Card>
      ) : (
        <div className={tournamentGridClass}>
          {tournaments.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              className={tournamentCardClass}
              canManage={showCreateActions}
              onView={handleViewTournament}
              onDelete={setTournamentToDelete}
            />
          ))}
        </div>
      )}
    </>
  );

  if (layout === 'page') {
    if (loading) {
      return (
        <div className={TOURNAMENTS_PAGE_SHELL}>
          <div className={TOURNAMENTS_PAGE_INNER}>
            <Typography variant="body" className="text-center">
              Loading tournaments...
            </Typography>
          </div>
        </div>
      );
    }

    return (
      <div className={TOURNAMENTS_PAGE_SHELL}>
        <ConfirmModal
          isOpen={!!tournamentToDelete}
          onClose={() => setTournamentToDelete(null)}
          onConfirm={handleDeleteTournament}
          title="Delete Tournament"
          message={
            tournamentToDelete?.has_scores
              ? `Delete "${tournamentToDelete?.name}"? This will archive the tournament (scores and results are preserved but it will be hidden from the list).`
              : `Delete "${tournamentToDelete?.name}"? This will permanently remove the tournament and all its data. This cannot be undone.`
          }
          confirmText={deleting ? 'Deleting...' : 'Delete'}
          confirmDisabled={deleting}
          variant="danger"
        />
        <div className={TOURNAMENTS_PAGE_INNER}>
          <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <BackButton hideOnDesktop className="mb-2" onClick={handleBack}>
                {backButtonLabel}
              </BackButton>
              <Heading level={1} className="mb-1 text-xl font-bold text-text-primary sm:text-2xl">
                Tournaments
              </Heading>
              <Text className="text-sm text-text-secondary">
                Create and manage badminton tournaments
              </Text>
            </div>
            {showCreateActions && (
              <Button
                variant="primary"
                className="w-full shrink-0 sm:w-auto"
                onClick={() => navigate(`/club/${clubId}/tournaments/new`)}
              >
                Create Tournament
              </Button>
            )}
          </div>

          {listBody}
        </div>
      </div>
    );
  }

  // embed (club tab)
  if (loading) {
    return (
      <div className="py-12 text-center">
        <Typography variant="body" className="text-text-muted">
          Loading tournaments...
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <ConfirmModal
        isOpen={!!tournamentToDelete}
        onClose={() => setTournamentToDelete(null)}
        onConfirm={handleDeleteTournament}
        title="Delete Tournament"
        message={
          tournamentToDelete?.has_scores
            ? `Delete "${tournamentToDelete?.name}"? This will archive the tournament (scores and results are preserved but it will be hidden from the list).`
            : `Delete "${tournamentToDelete?.name}"? This will permanently remove the tournament and all its data. This cannot be undone.`
        }
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        confirmDisabled={deleting}
        variant="danger"
      />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <Heading level={2} className="mb-1">
            Tournaments
          </Heading>
          <Text className="text-text-secondary text-sm">
            Tournaments and competitions for this club.
          </Text>
          {canManageTournaments ? null : (
            <Text className="text-text-muted text-sm mt-2">
              Only club management (owner, admin, or host) can create tournaments.
            </Text>
          )}
        </div>
        {showCreateActions && (
          <Button
            variant="primary"
            className="shrink-0 w-full sm:w-auto"
            onClick={() => navigate(`/club/${clubId}/tournaments/new`)}
          >
            Create Tournament
          </Button>
        )}
      </div>

      {listBody}

      {layout === 'embed' && tournaments.length > 1 ? (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
          Swipe for more
        </Typography>
      ) : null}
    </div>
  );
};

export default ClubTournamentList;
